import { ref } from 'vue';
import {
  type ChatStatusUpdate,
  type Interrupt,
  type SupportedLogLevels,
  type ThreadInfo,
  SupportedLogLevels as SupportedLogLevelsEnum,
  WebsocketSubscriptionTarget,
} from '@/api';
import {
  getWsClient,
  type ChatKind,
  type ChatResumeRequestBody,
  type ChatStreamRequestBody,
  type ChatRunHandler,
  type WebsocketChatIncomingMessage,
} from '@/services/WebSocketService';

export class ChatStreamCancelledError extends Error {
  constructor(message = 'Chat run cancelled') {
    super(message);
    this.name = 'ChatStreamCancelledError';
  }
}

export class ChatStreamConnectionError extends Error {
  constructor(message = 'Chat WebSocket connection closed') {
    super(message);
    this.name = 'ChatStreamConnectionError';
  }
}

/**
 * The run failed on the server. `message` is the backend's user-facing text — a
 * fixed string per failure class (provider still unavailable after its retries,
 * daily usage limit reached, no permission, generic) — so callers show it as is.
 */
export class ChatStreamServerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChatStreamServerError';
  }
}

export interface UseChatStreamOptions {
  /** Which chat backend to stream against. Defaults to `general`. */
  kind?: ChatKind;
  onToken?: (text: string) => void;
  onThreadInfo?: (info: ThreadInfo) => void;
  onStatus?: (status: ChatStatusUpdate) => void;
  onInterrupt?: (interrupt: Interrupt) => void;
  onError?: (level: SupportedLogLevels, message: string) => void;
}

export interface UseChatStreamApi {
  startChat: (request: ChatStreamRequestBody) => Promise<void>;
  resumeChat: (request: ChatResumeRequestBody) => Promise<void>;
  /** Rejoin a run that is already executing on the worker. */
  attachRun: (runId: string) => Promise<void>;
  /** Kill the run: tells the backend to abort the worker. */
  cancelChat: () => void;
  /** Stop listening but leave the run executing on the worker. */
  detach: () => void;
  isActive: () => boolean;
}

/**
 * How a run gets hold of its frames. A fresh run generates an id and `send`s a
 * start/resume frame; rejoining an existing one brings its `runId` along and
 * `subscribe`s to the `chat_run` target instead.
 */
interface RunSetup {
  runId?: string;
  send?: (runId: string) => boolean;
  subscribe?: (runId: string) => () => void;
}

function generateRunId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useChatStream(options: UseChatStreamOptions = {}): UseChatStreamApi {
  const wsClient = getWsClient();
  const kind: ChatKind = options.kind ?? 'general';
  const activeRunId = ref<string | null>(null);

  type ActiveRun = {
    runId: string;
    resolve: () => void;
    reject: (error: Error) => void;
    unregisterHandler: () => void;
    unregisterStatus: () => void;
    unsubscribe?: () => void;
  };

  let activeRun: ActiveRun | null = null;

  function teardown(run: ActiveRun): void {
    run.unregisterHandler();
    run.unregisterStatus();
    run.unsubscribe?.();
    if (activeRun === run) {
      activeRun = null;
      activeRunId.value = null;
    }
  }

  function handleIncoming(message: WebsocketChatIncomingMessage, run: ActiveRun): void {
    switch (message.type) {
      case 'chat_token': {
        options.onToken?.(message.data.content);
        return;
      }
      case 'chat_thread_info': {
        options.onThreadInfo?.(message.data);
        return;
      }
      case 'chat_status': {
        options.onStatus?.(message.data);
        return;
      }
      case 'chat_interrupt': {
        options.onInterrupt?.(message.data);
        return;
      }
      case 'chat_done': {
        teardown(run);
        run.resolve();
        return;
      }
      case 'chat_error': {
        options.onError?.(message.level, message.message);
        teardown(run);
        run.reject(new ChatStreamServerError(message.message));
        return;
      }
      default: {
        // Unhandled chat message type — ignore.
      }
    }
  }

  async function beginRun(setup: RunSetup): Promise<void> {
    if (activeRun) {
      // A fresh run supersedes the previous one — the backend rejects a second
      // concurrent run on the same thread, so the old one has to die. Merely
      // rejoining another run must never kill what is already executing.
      if (setup.send) {
        cancelChat();
      } else {
        detach();
      }
    }

    const opened = await wsClient.openConnection();
    if (!opened) {
      throw new ChatStreamConnectionError('Could not open WebSocket connection for chat');
    }

    const runId = setup.runId ?? generateRunId();

    return new Promise<void>((resolve, reject) => {
      const handler: ChatRunHandler = message => {
        if (!activeRun || activeRun.runId !== runId) return;
        handleIncoming(message, activeRun);
      };

      const unregisterHandler = wsClient.registerChatRun(runId, handler);
      const unregisterStatus = wsClient.addStatusListener(state => {
        if (state === 'open') return;
        if (!activeRun || activeRun.runId !== runId) return;
        const error = new ChatStreamConnectionError(
          state === 'error' ? 'Chat WebSocket error during run' : 'Chat WebSocket closed during run'
        );
        options.onError?.(SupportedLogLevelsEnum._40, error.message);
        teardown(activeRun);
        reject(error);
      });

      activeRun = {
        runId,
        resolve,
        reject,
        unregisterHandler,
        unregisterStatus,
        unsubscribe: setup.subscribe?.(runId),
      };
      activeRunId.value = runId;

      const sent = setup.send ? setup.send(runId) : true;
      if (!sent) {
        const error = new ChatStreamConnectionError('Failed to send chat frame over WebSocket');
        teardown(activeRun);
        reject(error);
      }
    });
  }

  async function startChat(request: ChatStreamRequestBody): Promise<void> {
    await beginRun({ send: runId => wsClient.sendChatStart(runId, request, kind) });
  }

  async function resumeChat(request: ChatResumeRequestBody): Promise<void> {
    await beginRun({ send: runId => wsClient.sendChatResume(runId, request, kind) });
  }

  /**
   * Rejoin a run that is already executing on the worker (the thread GET
   * reported an `active_run`). Subscribing to the `chat_run` target reopens that
   * run's frame stream on this connection. With no cursor the run replays from
   * its first frame — byte-identical to the original live sequence, `thread_info`
   * and status groups included — so the caller must attach to a **freshly reset**
   * bubble or a mid-answer rejoin duplicates the text so far. (A reconnect mid-run
   * is the other case, and it does carry a cursor: see `resubscribeChatRuns`.)
   * The post-run thread GET stays as belt-and-braces recovery.
   */
  async function attachRun(runId: string): Promise<void> {
    await beginRun({
      runId,
      subscribe: uid => wsClient.addSubscriptionUid(WebsocketSubscriptionTarget.CHAT_RUN, uid),
    });
  }

  function cancelChat(): void {
    if (!activeRun) return;
    const run = activeRun;
    // Best-effort tell the server to cancel; ignore send result.
    wsClient.sendChatCancel(run.runId);
    teardown(run);
    run.reject(new ChatStreamCancelledError());
  }

  /**
   * Stop following the run without killing it. `chat_cancel` is an explicit kill
   * now that runs execute on a Celery worker, so leaving the view (unmount,
   * thread switch, reset) must detach instead — the run keeps going and the
   * thread is flagged for attention when it settles.
   */
  function detach(): void {
    if (!activeRun) return;
    const run = activeRun;
    teardown(run);
    run.reject(new ChatStreamCancelledError('Chat run detached'));
  }

  function isActive(): boolean {
    return activeRun !== null;
  }

  return { startChat, resumeChat, attachRun, cancelChat, detach, isActive };
}
